import { useState, type ReactNode } from 'react';
import { Button, FormNumberInput, Modal } from '@/components/UI';
import { parseNumericDraft, toNumericDraft } from '@/components/UI/numericDraft';
import {
    AERO_RESULT_FILTER_GROUPS,
    AERO_RESULT_FILTER_PRESETS,
    type AeroActiveFilterChip,
    type AeroResultFilterGroup,
    type AeroResultFilterKey,
    type AeroResultFilterPreset,
    type AeroResultFilters,
    applyAeroFilterPreset,
    buildActiveAeroFilterChips,
    clearAeroFilterPreset,
    countActiveAeroResultFilters,
    isAeroFilterPresetActive
} from '../aeroResultFilters';
import { formatAeroCompactNumber } from '../aeroPageUtils';

interface AeroResultsFiltersControlProps {
    filters: AeroResultFilters;
    resultCount: number;
    totalResultCount: number;
    onChange: (key: AeroResultFilterKey, value: string) => void;
    onReplace: (filters: AeroResultFilters) => void;
    onClear: () => void;
}

const FilterChip = ({
    active,
    children,
    title,
    onClick
}: {
    active: boolean;
    children: ReactNode;
    title?: string;
    onClick: () => void;
}) => (
    <button
        aria-pressed={active}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
            active ? 'border-cyan-500 bg-cyan-600 text-white' : 'border-zinc-700 bg-black/30 text-zinc-300 hover:bg-black/50'
        }`}
        title={title}
        type="button"
        onClick={onClick}
    >
        {children}
    </button>
);

const ActiveFilterPill = ({ chip, onRemove }: { chip: AeroActiveFilterChip; onRemove: () => void }) => (
    <span className="inline-flex items-center gap-1 rounded-full border border-cyan-700/60 bg-cyan-900/30 px-2.5 py-1 text-xs text-cyan-100">
        {chip.label}
        <button
            aria-label={`Remove ${chip.label}`}
            className="ml-0.5 rounded-full text-cyan-200 hover:bg-cyan-800/50 hover:text-white"
            type="button"
            onClick={onRemove}
        >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} />
            </svg>
        </button>
    </span>
);

const RangeRow = ({
    group,
    filters,
    onChange
}: {
    group: AeroResultFilterGroup;
    filters: AeroResultFilters;
    onChange: (key: AeroResultFilterKey, value: string) => void;
}) => {
    const unit = group.valueLabel ? ` (${group.valueLabel})` : '';

    return (
        <div>
            <div className="mb-1 text-sm font-medium text-zinc-200">
                {group.label}
                {unit}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <FormNumberInput
                    noMargin
                    aria-label={`Minimum ${group.label}`}
                    placeholder="Min"
                    step={group.step}
                    value={parseNumericDraft(filters[group.minKey])}
                    onValueChange={(value) => onChange(group.minKey, toNumericDraft(value))}
                />
                <FormNumberInput
                    noMargin
                    aria-label={`Maximum ${group.label}`}
                    placeholder="Max"
                    step={group.step}
                    value={parseNumericDraft(filters[group.maxKey])}
                    onValueChange={(value) => onChange(group.maxKey, toNumericDraft(value))}
                />
            </div>
        </div>
    );
};

const AeroResultsFiltersControl = ({
    filters,
    resultCount,
    totalResultCount,
    onChange,
    onReplace,
    onClear
}: AeroResultsFiltersControlProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const activeChips = buildActiveAeroFilterChips(filters);
    const activeFilterCount = countActiveAeroResultFilters(filters);
    const hasActive = activeFilterCount > 0;

    const togglePreset = (preset: AeroResultFilterPreset) => {
        if (isAeroFilterPresetActive(filters, preset)) {
            onReplace(clearAeroFilterPreset(filters, preset));
            return;
        }

        onReplace(applyAeroFilterPreset(filters, preset));
    };

    const removeChip = (chip: AeroActiveFilterChip) => {
        const next: AeroResultFilters = { ...filters, [chip.minKey]: '', [chip.maxKey]: '' };
        onReplace(next);
    };

    return (
        <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline-dark" onClick={() => setIsModalOpen(true)}>
                    <span className="inline-flex items-center gap-1.5">
                        <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M3 4h18M6 12h12M10 20h4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                        </svg>
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="ml-1 rounded-full bg-cyan-600 px-1.5 text-[10px] font-semibold text-white">
                                {activeFilterCount}
                            </span>
                        )}
                    </span>
                </Button>

                <div aria-hidden="true" className="hidden h-5 w-px bg-zinc-800 sm:block" />

                <span className="text-[11px] uppercase tracking-wide text-zinc-500">Presets</span>
                {AERO_RESULT_FILTER_PRESETS.map((preset) => {
                    const active = isAeroFilterPresetActive(filters, preset);

                    return (
                        <FilterChip active={active} key={preset.id} title={preset.description} onClick={() => togglePreset(preset)}>
                            {preset.label}
                        </FilterChip>
                    );
                })}

                <div className="ml-auto text-xs text-zinc-500">
                    {hasActive
                        ? `${formatAeroCompactNumber(resultCount)} of ${formatAeroCompactNumber(totalResultCount)}`
                        : `${formatAeroCompactNumber(totalResultCount)} rows`}
                </div>
            </div>

            {activeChips.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {activeChips.map((chip) => (
                        <ActiveFilterPill chip={chip} key={chip.id} onRemove={() => removeChip(chip)} />
                    ))}
                    <button
                        className="ml-1 text-[11px] uppercase tracking-wide text-zinc-500 hover:text-zinc-300"
                        type="button"
                        onClick={onClear}
                    >
                        Clear all
                    </button>
                </div>
            )}

            <Modal isOpen={isModalOpen} size="lg" title="Filter results" onClose={() => setIsModalOpen(false)}>
                <div className="space-y-4">
                    <div>
                        <div className="mb-2 text-[11px] uppercase tracking-wide text-zinc-500">Presets</div>
                        <div className="flex flex-wrap gap-2">
                            {AERO_RESULT_FILTER_PRESETS.map((preset) => {
                                const active = isAeroFilterPresetActive(filters, preset);

                                return (
                                    <FilterChip
                                        active={active}
                                        key={preset.id}
                                        title={preset.description}
                                        onClick={() => togglePreset(preset)}
                                    >
                                        {preset.label}
                                    </FilterChip>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {AERO_RESULT_FILTER_GROUPS.map((group) => (
                            <RangeRow filters={filters} group={group} key={group.label} onChange={onChange} />
                        ))}
                    </div>

                    <div className="flex items-center justify-between gap-2 border-t border-zinc-800 pt-3">
                        <div className="text-xs text-zinc-500">
                            {hasActive
                                ? `Showing ${formatAeroCompactNumber(resultCount)} of ${formatAeroCompactNumber(totalResultCount)} rows.`
                                : `Showing ${formatAeroCompactNumber(totalResultCount)} rows.`}
                        </div>
                        <div className="flex gap-2">
                            <Button disabled={!hasActive} size="sm" variant="outline-dark" onClick={onClear}>
                                Clear filters
                            </Button>
                            <Button size="sm" onClick={() => setIsModalOpen(false)}>
                                Done
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AeroResultsFiltersControl;
